import React, { useEffect } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { AuthProvider } from './contexts/AuthContext';
import { PollsProvider } from './contexts/PollsContext';
import { VideoProvider } from './contexts/VideoContext';
import { NetworkStatus } from './components/common/NetworkStatus';
import { videoUpdateManager } from './services/videoUpdateManager';
import { AppRouter } from './router/RouterConfig';
import { darkTheme } from './theme';
import CookieConsentManager from './components/cookies/CookieConsentManager';
import { setupPushNotificationListeners, setupTokenRefreshHandler } from './services/pushNotificationService';

// Move the declare global block to the top level of the file, outside any function
declare global {
  interface Window {
    dataLayer: any[];
  }
}

function loadTagManager() {
  if (document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) return;
  const script = document.createElement('script');
  script.src = 'https://www.googletagmanager.com/gtag/js?id=G-MS1V27GGDX';
  script.async = true;
  document.body.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) { window.dataLayer.push(args); }
  gtag('js', new Date());
  gtag('config', 'G-MS1V27GGDX');
}

function loadAdSenseScript() {
  if (document.querySelector('script[src*="adsbygoogle.js"]')) return;
  const script = document.createElement('script');
  script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-2967060688498429';
  script.async = true;
  script.crossOrigin = 'anonymous';
  document.body.appendChild(script);
}

function App() {
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Initialize video service
        if (!videoUpdateManager.isRunning()) {
          await videoUpdateManager.startUpdates(120);
        }

        // Initialize push notification handlers
        setupPushNotificationListeners();
        setupTokenRefreshHandler();

        if (localStorage.getItem('cookie_consent_accepted') === 'true') {
          loadTagManager();
          loadAdSenseScript();
        }
      } catch (error) {
        console.error('Error initializing services:', error);
      }
    };

    initializeApp();
    return () => {
      videoUpdateManager.stopUpdates();
    };
  }, []);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <SnackbarProvider 
        maxSnack={3}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <AuthProvider>
          <PollsProvider>
            <VideoProvider>
              <AppRouter />
              <CookieConsentManager />
              <NetworkStatus />
            </VideoProvider>
          </PollsProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App; 